import { useState } from 'react';
import { getRequestQuotesByCommunityId } from '@/api/requests';
import { toTitleCase } from '@/utils/to-title-case';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Notepad as NotepadIcon,
  Plus as PlusIcon,
  Paperclip as PaperclipIcon,
  PencilSimple as PencilIcon,
  CheckSquare as ReviewIcon,
  Trash as TrashIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import axios from '@/lib/v2/axios';
import { toast } from '@/components/core/v2/toaster';

import type { File as QuoteFile } from '@/types/file';
import type { Quote } from '@/types/v2/quote';
import type { Request } from '@/types/v2/request';
import { Quote_Status } from '@/types/v2/quote';
import ReviewQuoteDialog from '@/pages/request/dialogs/ReviewQuoteDialog';
import ViewQuoteDialog from '@/pages/request/dialogs/ViewQuoteDialog';
import CreateUpdateQuoteDialog from '@/pages/v2/requests/request/components/create-quote-dialog';
import { dayjs } from '@/lib/v2/dayjs';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { SectionLabel } from './request-details-card';

interface RequestQuotesData {
  quotes: Quote[];
}

interface RequestQuotesProps {
  request?: Request | null;
}

export function RequestQuotes({ request }: RequestQuotesProps): React.JSX.Element | null {
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedFile, setSelectedFile] = useState<QuoteFile | null>(null);
  const { community } = useUser();
  const { requestId } = useParams();
  const { hasBoardLevelAccess } = useCommunityRole();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<RequestQuotesData>({
    queryKey: ['request-quotes', requestId],
    queryFn: async () => {
      return await getRequestQuotesByCommunityId(community?.id, requestId, {
        params: { include: 'status,meeting' },
      });
    },
    enabled: hasBoardLevelAccess,
  });

  const quoteDialog = useDialog();
  const reviewQuoteDialog = useDialog();
  const viewQuoteDialog = useDialog();

  const deleteQuoteMutation = useMutation({
    mutationFn: async (quoteId: string | number) => {
      await axios.delete(`/api/v1/requests/${requestId}/quotes/${quoteId}`);
    },
    onSuccess: () => {
      toast.success('Quote deleted');
      queryClient.invalidateQueries({ queryKey: ['request-quotes', requestId] });
    },
    onError: () => {
      toast.error('Failed to delete quote');
    },
  });

  if (!hasBoardLevelAccess) return null;

  const quotes = data?.quotes ?? [];

  return (
    <>
      <Box>
        <SectionLabel
          icon={<NotepadIcon size={14} />}
          action={
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                quoteDialog.handleOpen();
                setSelectedQuote(null);
              }}
              size="small"
              startIcon={<PlusIcon size={12} />}
              sx={{ fontSize: '0.75rem', textTransform: 'none', fontWeight: 500, minWidth: 0, px: 1 }}
            >
              Add Quote
            </Button>
          }
        >
          Quotes
        </SectionLabel>

        <Box sx={{ mt: 1.5 }}>
          {isLoading ? (
            <Typography color="text.disabled" variant="body2">Loading...</Typography>
          ) : quotes.length === 0 ? (
            <Typography color="text.disabled" sx={{ fontStyle: 'italic' }} variant="body2">
              No quotes yet
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={0}>
              {quotes.map((quote) => (
                <Box key={quote.id} sx={{ py: 1.5 }}>
                  {/* Top row: amount + status */}
                  <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                      ${parseFloat(quote.total)?.toFixed(2)}
                    </Typography>
                    <Tooltip title={quote.status?.description ?? ''}>
                      <Chip
                        label={toTitleCase(quote.status?.name ?? '')}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: `${quote.status?.color ?? '#6B7280'}20`,
                          color: quote.status?.color ?? '#6B7280',
                          border: `1px solid ${quote.status?.color ?? '#6B7280'}40`,
                        }}
                      />
                    </Tooltip>
                  </Stack>

                  {/* Date + description */}
                  <Typography color="text.disabled" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    {dayjs(quote.created_at).format('MMM D, YYYY')}
                  </Typography>
                  {quote.description && (
                    <Typography
                      color="text.secondary"
                      sx={{
                        fontSize: '0.8125rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                      variant="body2"
                    >
                      {quote.description}
                    </Typography>
                  )}

                  {/* Actions */}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    {quote.files?.[0] && (
                      <Tooltip title="View attached document">
                        <IconButton
                          onClick={() => {
                            setSelectedQuote(quote);
                            setSelectedFile(
                              quote.files?.[0]
                                ? { ...quote.files[0], mime_type: quote.files[0].type || '' }
                                : null
                            );
                            viewQuoteDialog.handleOpen();
                          }}
                          size="small"
                        >
                          <PaperclipIcon size={15} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {quote.status_id === Quote_Status.PENDING && (
                      <Tooltip title="Edit quote">
                        <IconButton
                          onClick={() => { setSelectedQuote(quote); quoteDialog.handleOpen(); }}
                          size="small"
                        >
                          <PencilIcon size={15} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Approve or reject quote">
                      <IconButton
                        onClick={() => { setSelectedQuote(quote); reviewQuoteDialog.handleOpen(); }}
                        size="small"
                        color="primary"
                      >
                        <ReviewIcon size={15} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete quote">
                      <IconButton
                        onClick={() => deleteQuoteMutation.mutate(quote.id)}
                        size="small"
                        color="error"
                        disabled={deleteQuoteMutation.isPending}
                      >
                        <TrashIcon size={15} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <CreateUpdateQuoteDialog
        communityId={community?.id ?? ''}
        onClose={quoteDialog.handleClose}
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ['request-quotes', requestId] })}
        open={quoteDialog.open}
        quote={selectedQuote}
        requestId={requestId ?? ''}
        requestNumericId={request?.id != null ? Number(request.id) : undefined}
      />
      <ReviewQuoteDialog
        onClose={reviewQuoteDialog.handleClose}
        onRefresh={() => { queryClient.invalidateQueries({ queryKey: ['request-quotes', requestId] }); }}
        open={reviewQuoteDialog.open}
        quote={selectedQuote}
      />
      <ViewQuoteDialog file={selectedFile} onClose={viewQuoteDialog.handleClose} open={viewQuoteDialog.open} />
    </>
  );
}
